import React, { createContext, useState } from "react";
import { get, post, destroy } from "@rails/request.js";

// create context for the community pages
const CommunityContext = createContext({
  state: {},
  actions: {
    fetchPhotos: () => {},
    likePhoto: () => {},
    deletePhoto: () => {},
    nextPrevPhoto: () => {},
    setCurrentPage: () => {},
    setCurrentPhoto: () => {},
    setCurrentTab: () => {},
    setState: () => {},
    setLoading: () => {},
    setProcessing: () => {},
    toggleModal: () => {},
    toggleShare: () => {},
    togglePhotoModal: () => {},
    toggleSlideOver: () => {},
    sendAnalytics: (eventName, eventData = {}) => {},
  },
});

const MODAL_TYPES = {
  PERMISSION_ONLY: "permission-only",
  FULL_VERSION: "full-version",
  UPLOAD_PHOTOS: "upload-photos",
};

const NEXT_STEP = {
  ALL_PHOTOS: "all-photos",
  MY_PHOTOS: "my-photos",
  UPLOAD_PHOTOS: "upload-photos",
  LIKE_PHOTO: "like-photo",
  PAGINATION: "pagination",
};

const TABS = [
  {
    name: "My Photos",
    id: "my-photos",
  },
  {
    name: "All Photos",
    id: "all-photos",
  },
];

const CommunityProvider = (props) => {
  // default state
  const [state, setState] = useState({
    ...props.value,
    community_id: null,
    content: props.value.content,
    currentTab: "all-photos",
    currentPage: 1,
    fbAppId: props.value.fbAppId,
    modalType: MODAL_TYPES.PERMISSION_ONLY,
    nextStep: null,
    isAuth: false,
    isModalOpen: false,
    isShareOpen: false,
    isPhotoModalOpen: false,
    sharePhoto: {},
    viewPhoto: {},
    isLoading: true,
    isProcessing: false,
    photos: [],
    user: null,
    uploaderConfig: {},
    RECAPTCHA_SITE_KEYv2: props.value.RECAPTCHA_SITE_KEYv2,
    optInFormIsValid: false,
    notifications: [],
    paginationMeta: {},
    edition: props.value.edition,
    isSlideOverOpen: false,
  });

  const setLikedPhoto = ({ liked, id, like_count, isLikingPhoto }) => {
    let photo;
    // map photos for updated state
    const photos = state.photos.map((p) => {
      if (p.id !== id) return p;
      // else
      photo = p;

      return {
        ...photo,
        like_count,
        liked_by_current_user: liked,
      };
    });

    // update state
    setState((state) => ({
      ...state,
      photos,
      viewPhoto: {
        ...photo,
        like_count,
        liked_by_current_user: liked,
        isLikingPhoto,
      },
    }));
  };

  // like photo api call
  const likePhoto = async ({ id, liked, like_count }) => {
    try {
      setLikedPhoto({ id, liked: !liked, like_count, isLikingPhoto: true });

      const url = `/api/v1/community/${id}/like_photo`;
      const payload = {
        body: JSON.stringify({ liked: !liked }),
      };

      // post request to like photo
      const response = await post(url, payload);
      if (response.ok) {
        // parse response
        const { like_count: new_count, liked_by_current_user } =
          await response.json;

        setLikedPhoto({
          id,
          liked: liked_by_current_user,
          like_count: new_count,
          isLikingPhoto: false,
        });
      } else {
        setLikedPhoto({ id, liked, like_count, isLikingPhoto: false });
        throw new Error(response);
      }
    } catch (error) {
      console.error("Error", error);
    }
  };

  const deletePhoto = async ({ deletedPhoto: { id } }) => {
    try {
      setProcessing(true);

      const url = `/api/v1/community/${id}/delete_photo`;
      const response = await destroy(url);

      if (response.ok) {
        await fetchPhotos(state.currentPage, state.currentTab);
        setProcessing(false);
      } else {
        throw new Error(response);
      }
    } catch (error) {
      console.error("Error", error);
    }
  };

  // fetch photos api call
  const fetchPhotos = async (page = 1, tab = "all-photos") => {
    try {
      setLoading(true);

      const activeTab = tab ? tab : state.currentTab;
      const url = `/api/v1/community?page=${page}&tab=${activeTab}&edition=${state.edition}`;
      const response = await get(url);

      if (response.ok) {
        const { uploaderConfig, photos, user, notifications, paginationMeta } =
          await response.json;
        // save state in context
        setState((state) => ({
          ...state,
          currentTab: tab,
          currentPage: page,
          photos,
          user,
          uploaderConfig,
          notifications,
          paginationMeta,
          viewPhoto: {
            ...photos[0],
          },
        }));
      } else {
        throw new Error(response);
      }
    } catch (error) {
      console.error("Error", error);
      setState((state) => ({ ...state, error, isLoading: false }));
    } finally {
      setLoading(false);
    }
  };

  const setCurrentTab = (tab) =>
    setState((state) => ({ ...state, currentTab: tab }));

  const setLoading = (isLoading) =>
    setState((state) => ({ ...state, isLoading }));

  const setProcessing = (isProcessing) =>
    setState((state) => ({ ...state, isProcessing, isPhotoModalOpen: false }));

  const toggleModal = (modalType, nextStep, options) =>
    setState((state) => ({
      ...state,
      modalType,
      nextStep,
      nextPage: options?.page,
      likedPhoto: options?.likedPhoto,
      isModalOpen: !state.isModalOpen,
    }));

  const toggleShare = ({
    id,
    caption,
    fullscreen,
    handle,
    thumbnail_large,
  }) => {
    setState((state) => ({
      ...state,
      sharePhoto: { id, caption, fullscreen, handle, thumbnail_large },
      isShareOpen: !state.isShareOpen,
    }));
  };

  const nextPrevPhoto = (photo) =>
    setState((state) => ({
      ...state,
      viewPhoto: {
        ...photo,
      },
    }));

  const setCurrentPhoto = (photo) => {
    setState((state) => ({
      ...state,
      viewPhoto: {
        ...photo,
      },
    }));
  };

  const togglePhotoModal = () =>
    setState((state) => ({
      ...state,
      isPhotoModalOpen: !state.isPhotoModalOpen,
    }));

  const toggleSlideOver = () => {
    setState((state) => ({
      ...state,
      isSlideOverOpen: !state.isSlideOverOpen,
    }));
  };

  const sendAnalytics = (eventName, eventData = {}) => {
    if (!window.analytics) return;

    // only create one instance of TgAnalytics
    let a;
    if (!state.tgAnalytics) {
      a = new TgAnalytics();
      setState((state) => ({
        ...state,
        tgAnalytics: a,
      }));
    } else {
      a = state.tgAnalytics;
    }
    // send analytics event
    analytics.track(eventName, {
      ...a.pageData,
      ...eventData,
      event_generated_at: new Date().toISOString(),
    });
  };

  const setCurrentPage = (currentPage) =>
    setState((state) => ({ ...state, currentPage }));

  const actions = {
    fetchPhotos,
    likePhoto,
    deletePhoto,
    nextPrevPhoto,
    setLoading,
    setProcessing,
    setState,
    setCurrentPage,
    setCurrentPhoto,
    setCurrentTab,
    toggleModal,
    toggleShare,
    togglePhotoModal,
    toggleSlideOver,
    sendAnalytics,
  };

  // pass state, actions into the context provider
  return (
    <CommunityContext.Provider value={{ state, actions }}>
      {props.children}
    </CommunityContext.Provider>
  );
};

export { CommunityContext, CommunityProvider, MODAL_TYPES, NEXT_STEP, TABS };



// WEBPACK FOOTER //
// ./app/javascript/bundles/community/context.jsx